// HomePage.tsx

    import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * HomePage – minimal full-screen canvas with ONLY 3D text.
 * Interactions: mouse movement tilts the text.
 * Mobile-friendly, animated gradient inside the shadow extrusion instead of the front fill.
 */

export default function HomePage() {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    let raf = 0;
    let target = { rx: 0, ry: 0 };
    let current = { rx: 0, ry: 0 };

    const onMouseMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      target.ry = (x - 0.5) * 18;
      target.rx = -(y - 0.5) * 12;
    };

    const loop = () => {
      current.rx += (target.rx - current.rx) * 0.08;
      current.ry += (target.ry - current.ry) * 0.08;
      setTilt({ ...current });
      raf = requestAnimationFrame(loop);
    };

    el.addEventListener("mousemove", onMouseMove);
    raf = requestAnimationFrame(loop);
    return () => {
      el.removeEventListener("mousemove", onMouseMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="min-h-screen w-full flex items-center justify-center p-[max(env(safe-area-inset-top),0px)]"
      style={{
        backgroundColor: "#0f172a",
      }}
    >
      <Interactive3DText text="OC CARTERS" depth={42} rx={tilt.rx} ry={tilt.ry} />

      <style>{`
        @keyframes shadow-pan {
          0% { background-position: 0% 50%; }
          50% { background-position: 200% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  );
}

function Interactive3DText({
  text,
  depth = 32,
  rx = 0,
  ry = 0,
}: {
  text: string;
  depth?: number;
  rx?: number;
  ry?: number;
}) {
  const transform = `perspective(1000px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg)`;

  return (
    <div
      className="relative select-none px-4"
      style={{ transform, transformStyle: "preserve-3d", willChange: "transform" }}
      aria-label={text}
    >
      {/* FRONT: plain solid text */}
      <span
        className="block text-center font-black leading-none tracking-tight text-slate-100"
        style={{
          fontSize: "clamp(2.5rem, 12vw, 9rem)",
          letterSpacing: "0.02em",
          lineHeight: 1.0,
        }}
      >
        {text}
      </span>

      {/* BACK: extrusion shadow with animated gradient */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 text-center font-black leading-none tracking-tight select-none"
        style={{
          fontSize: "clamp(2.5rem, 12vw, 9rem)",
          letterSpacing: "0.02em",
          lineHeight: 1.0,
          color: "transparent",
          backgroundImage:
            "linear-gradient(90deg, #a5f3fc, #38bdf8, #3b82f6, #8b5cf6, #ec4899, #f59e0b, #22d3ee, #14b8a6, #a5f3fc)",
          backgroundSize: "400% 100%",
          backgroundRepeat: "no-repeat",
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          animation: "shadow-pan 12s linear infinite",
          filter: "blur(2px) opacity(0.8)",
        }}
      >
        {text}
      </span>
    </div>
  );
}